import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAuthState, LoginPayload } from 'interfaces/auth.interfaces';
import { REFRESH_TOKEN, ACCESS_TOKEN } from 'constant';
import { RootState } from 'app/store';
import { UserData } from 'interfaces/UserData.interface';
import { v4 } from 'uuid';
import { Transaction } from 'interfaces/Transaction.interface';
import { Notification } from 'interfaces/Notification.interface';
import { getAllNotificationsUser } from 'APIs/Notification.Api';
import { NftType } from 'interfaces/NftData.interface';
import { SubscribeData } from 'interfaces/Subscribe.interface';

export const initialState: IAuthState = {
  id: undefined,
  name: undefined,
  username: undefined,
  email: undefined,
  bio: undefined,
  avatar: undefined,
  seenOnBoarding: false,
  walletAddress: undefined,
  authenticated: false,
  inboxUsers: [],
  generalChats: [],
  canPost: false,
  isCreator: false,
  following: undefined,
  followers: undefined,
  subscribers: undefined,
  subscribedTo: undefined,
  backstageDesc: undefined,
  fileUploadProgess: 0,
  featured: false,
  verified: false,
  order: 0,
  role: 'client',
  invites: 0,
  transactionsSent: undefined,
  transactionsReceived: undefined,
  inTransaction: false,
  notificationsReceived: [],
  notificationsSent: [],
  isTopFeatured: false,
  subscriptionPrice: undefined,
  isOnTrial: undefined,
  trialDuration: undefined,
  discountedSubscriptionPrice: undefined,
  numberOfSales: undefined,
  preferredCategories: undefined,
  invited: false,
  tatumUserId: undefined,
  tatumWalletAddress: undefined,
  requestedCreatorAccess: false,
};

export const getNotificationsUserState = createAsyncThunk(
  'notification?user=id',
  async (userId: number, thunkAPI) => {
    const notifications = await getAllNotificationsUser(userId);
    return notifications.data;
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state: IAuthState, action: PayloadAction<LoginPayload>): any => {
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.username = action.payload.username;
      state.email = action.payload.email;
      state.avatar = action.payload.avatar;
      state.seenOnBoarding = action.payload.seenOnBoarding;
      state.walletAddress = action.payload.walletAddress;
      state.canPost = action.payload.canPost;
      state.invites = action.payload.invites;
      state.isCreator = action.payload.isCreator;
      state.authenticated = true;
      state.followers = action.payload.followers;
      state.following = action.payload.following;
      state.subscribers = action.payload.subscribers;
      state.subscribedTo = action.payload.subscribedTo;
      state.backstageDesc = action.payload.backstageDesc;
      state.transactionsSent = action.payload.transactionsSent;
      state.transactionsReceived = action.payload.transactionsReceived;
      state.notificationsReceived = action.payload.notificationsReceived;
      state.isTopFeatured = action.payload.isTopFeatured;
      state.subscriptionPrice = action.payload.subscriptionPrice;
      state.isOnTrial = action.payload.isOnTrial;
      state.trialDuration = action.payload.trialDuration;
      state.preferredCategories = action.payload.preferredCategories;
      state.tatumUserId = action.payload.tatumUserId;
      state.tatumWalletAddress = action.payload.tatumWalletAddress;
      if (action.payload.access_token) {
        localStorage.setItem(ACCESS_TOKEN, action.payload.access_token);
      }
    },
    logout: (state: IAuthState) => {
      state.id = undefined;
      state.name = undefined;
      state.username = undefined;
      state.email = undefined;
      state.seenOnBoarding = false;
      state.avatar = undefined;
      state.walletAddress = undefined;
      state.authenticated = false;
      state.inboxUsers = [];
      state.generalChats = [];
      state.following = undefined;
      state.followers = undefined;
      state.invites = 0;
      localStorage.removeItem(ACCESS_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      window.location.reload();
    },
    setWallet: (
      state: IAuthState,
      action: PayloadAction<{ walletAddress: string }>
    ) => {
      state.walletAddress = action.payload.walletAddress;
    },
    addInboxUsersAuth: (state: IAuthState, action: PayloadAction<UserData>) => {
      if (state.inboxUsers.some(e => e.name === action.payload.name)) return;
      else state.inboxUsers.push(action.payload);
    },
    updateFollowing: (
      state: IAuthState,
      action: PayloadAction<{
        type: 'follow' | 'unfollow';
        userData: UserData;
        authData: UserData;
      }>
    ) => {
      const { type, userData, authData } = action.payload;
      if (state.following) {
        const userIndex = state.following.findIndex(
          following => following.following.id === userData.id
        );
        if (type === 'unfollow') {
          state.following[userIndex].unfollowed = true;
        } else {
          if (
            state.following.some(
              following =>
                following.following.username ===
                action.payload.userData.username
            )
          ) {
            state.following[userIndex].unfollowed = false;
          } else
            state.following.push({
              id: v4(),
              followedAt: new Date(),
              followedDate: new Date(),
              following: userData,
              follower: authData,
            });
        }
      } else {
        return;
      }
    },
    addGeneralUser: (state: IAuthState, action: PayloadAction<number>) => {
      if (
        state.generalChats.includes(action.payload) ||
        action.payload === state.id
      )
        return;
      else state.generalChats.push(action.payload);
    },
    addProgress: (state: IAuthState, action: PayloadAction<number>) => {
      state.fileUploadProgess = action.payload;
    },
    inviteCreator: (state: IAuthState) => {
      state.invites -= 1;
      state.isCreator = true;
    },
    addTransaction: (state: IAuthState, action: PayloadAction<Transaction>) => {
      if (state.transactionsReceived) {
        if (
          state.transactionsReceived.some(
            transaction => transaction.hash === action.payload.hash
          )
        )
          return;
        else state.transactionsReceived.push(action.payload);
      } else {
        state.transactionsReceived = [action.payload];
      }
    },
    removeTransaction: (
      state: IAuthState,
      action: PayloadAction<{ hash: string; postId?: number }>
    ) => {
      if (state.transactionsReceived) {
        const foundTransaction = state.transactionsReceived.findIndex(
          transaction => {
            if (action.payload.postId)
              return transaction.postId !== action.payload.postId;
            else return transaction.hash !== action.payload.hash;
          }
        );
        state.transactionsReceived[foundTransaction].isProcessed = true;
      }
    },
    updateTransactionState: (
      state: IAuthState,
      action: PayloadAction<boolean>
    ) => {
      state.inTransaction = action.payload;
    },
    addNotification: (
      state: IAuthState,
      action: PayloadAction<Notification>
    ) => {
      if (!state.notificationsReceived) {
        state.notificationsReceived = [action.payload];
      } else state.notificationsReceived.unshift(action.payload);
    },
    updateNotification: (
      state: IAuthState,
      action: PayloadAction<Notification[]>
    ) => {
      if (action.payload.length > 0) {
        action.payload.forEach(id => {
          const index = state.notificationsReceived.findIndex(
            notification => notification.id === id.id
          );
          state.notificationsReceived[index].isRead = true;
        });
      }
    },
    updateCategories: (state: IAuthState, action: PayloadAction<NftType[]>) => {
      state.preferredCategories = [...action.payload];
    },

    setInvited: (state: IAuthState) => {
      state.invited = true;
    },
    addSubscribedTo: (
      state: IAuthState,
      action: PayloadAction<SubscribeData>
    ) => {
      if (!state.subscribedTo) state.subscribedTo = [action.payload];
      else state.subscribedTo = [...state.subscribedTo, action.payload];
    },
    removeSubscribedTo: (state: IAuthState, action: PayloadAction<number>) => {
      state.subscribedTo = state.subscribedTo?.filter(
        sub => sub.id !== action.payload
      );
    },
    updateProfilePicture: (
      state: IAuthState,
      action: PayloadAction<string>
    ) => {
      state.avatar = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      getNotificationsUserState.fulfilled,
      (state, action: PayloadAction<Notification[]>) => {
        state.notificationsReceived = action.payload.reverse();
      }
    );
  },
});

// Action creators are generated for each case reducer function
export const {
  login,
  logout,
  setWallet,
  addInboxUsersAuth,
  addGeneralUser,
  addProgress,
  updateFollowing,
  inviteCreator,
  addTransaction,
  removeTransaction,
  updateTransactionState,
  addNotification,
  updateNotification,
  updateCategories,
  setInvited,
  addSubscribedTo,
  removeSubscribedTo,
  updateProfilePicture,
} = authSlice.actions;
export const selectAuth = (state: RootState): IAuthState => state.auth;

export default authSlice.reducer;
